// src/utils/lotCodeUtils.js
// Lot code generation based on a company-configurable format string.
//
// Supported tokens:
//   {YYYY}  — 4-digit year            (e.g. 2025)
//   {YY}    — 2-digit year            (e.g. 25)
//   {MM}    — 2-digit month number    (e.g. 03)
//   {MON}   — month letter A–L        (Pizzacini style, Jan=A … Dec=L)
//   {DD}    — 2-digit day             (e.g. 18)
//   {JUL}   — Julian day of year      (e.g. 077 for March 18)
//
// Default format (used when none is configured): {MON}-1{YY}{DD}1
// This preserves the original Pizzacini lot code for existing companies.

export const DEFAULT_LOT_CODE_FORMAT = '{MON}-1{YY}{DD}1';

// Human-readable legend for the default format, shown in tech sheets and settings.
export const DEFAULT_LOT_CODE_KEY = 'MON: month letter (A=Jan…L=Dec), YY: 2-digit year, DD: day of month';

const MONTH_LETTERS = 'ABCDEFGHIJKL';

/**
 * Generate a lot code string from a format template.
 * @param {string|null|undefined} format  Template string (falls back to default)
 * @param {Date} [date]                   Date to use (defaults to today)
 * @returns {string}
 */
export function generateLotCode(format, date = new Date()) {
  const fmt = format || DEFAULT_LOT_CODE_FORMAT;
  const year4 = String(date.getFullYear());
  const year2 = year4.slice(-2);
  const monthNum = String(date.getMonth() + 1).padStart(2, '0');
  const monthLetter = MONTH_LETTERS[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  const startOfYear = new Date(date.getFullYear(), 0, 0);
  const julianDay = String(Math.floor((date - startOfYear) / 86400000)).padStart(3, '0');

  return fmt
    .replace(/{YYYY}/g, year4)
    .replace(/{YY}/g, year2)
    .replace(/{MM}/g, monthNum)
    .replace(/{MON}/g, monthLetter)
    .replace(/{DD}/g, day)
    .replace(/{JUL}/g, julianDay);
}

/** Available tokens shown to users in the format builder */
export const LOT_CODE_TOKENS = [
  { token: '{JUL}',  label: 'Julian Day (001–366)' },
  { token: '{MON}',  label: 'Month Letter (A–L)' },
  { token: '{MM}',   label: 'Month Number (01–12)' },
  { token: '{DD}',   label: 'Day (01–31)' },
  { token: '{YY}',   label: '2-digit Year' },
  { token: '{YYYY}', label: '4-digit Year' },
];
